import logger from '@orn/shared-web/lib/logger';

/**
 * Debug log for the WS connection lifecycle. Routes through `logger.debug`
 * (which uses `console.debug` and skips Sentry) so the trail sits under
 * DevTools' "Verbose" filter — visible when investigating reconnect behavior,
 * quiet by default.
 */
export function debugLog(message: string, context?: Record<string, unknown>): void {
  const suffix = context ? ` ${JSON.stringify(context)}` : '';
  logger.debug(`[assistant-ws] ${message}${suffix}`);
}

/**
 * Structured fields for the close diagnostic log line. Surfaces the real close
 * `code`/`reason`/`wasClean` so an idle-timeout drop (expected `1006`) is
 * distinguishable from a server-initiated or clean close. Empty `reason`
 * collapses to `undefined` so it doesn't clutter the log.
 */
export function describeCloseEvent(event: Pick<CloseEvent, 'code' | 'reason' | 'wasClean'>): {
  code: number;
  reason: string | undefined;
  wasClean: boolean;
} {
  return { code: event.code, reason: event.reason || undefined, wasClean: event.wasClean };
}

export interface IAssistantPageActivityState {
  visibilityState: DocumentVisibilityState;
  hasFocus: () => boolean;
}

export function isAssistantPageActive(page: IAssistantPageActivityState): boolean {
  return page.visibilityState === 'visible' && page.hasFocus();
}

export type ReconnectAfterCloseDecision = 'none' | 'schedule' | 'defer';

export function getReconnectAfterCloseDecision(
  event: Pick<CloseEvent, 'code'>,
  pageActive: boolean
): ReconnectAfterCloseDecision {
  // 4009 = superseded by a newer tab. Terminal: auto-reconnecting would
  // re-claim the thread and evict the other tab, looping into an eviction
  // storm. The user re-claims explicitly via the "use here" action.
  //
  // 4004 = the household holds no active membership, never-started as well as
  // lapsed. Terminal for the same reason as 4003 beside it: reconnecting
  // cannot change the answer, so a retry would hammer the wall for as long as
  // the tab stayed open.
  if (event.code === 1000 || event.code === 4001 || event.code === 4003 || event.code === 4004 || event.code === 4009) {
    return 'none';
  }
  return pageActive ? 'schedule' : 'defer';
}
